import * as React from 'react'
import { FC, useEffect, useCallback, useState, useMemo } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { SideDrawerForm, InfoTooltip } from '@sales-center/components'
import { styles } from './hooks/useStyles'
import { SalesFunnelSectionList } from './SalesFunnelSectionList'
import { useSelector } from 'react-redux'
import {
  useCreateSalesFunnel,
  useGetSalesFunnel,
  useUpdateSalesFunnel,
} from '@user/store/salesFunnel/hooks'
import {
  selectEditFunnelDetail,
  setEditFunnelName,
  resetEditFunnel,
  selectAllEditFunnelSections,
  setEditFunnelOtherReasons,
} from '@user/store/salesFunnel/editSlice'
import { toast } from '@sales-center/components'
import Alert from '@mui/material/Alert'
import { nameLengthError } from '@user/store/salesFunnel'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'

interface Props {
  onClose: () => void
  editId?: string
}

export const AddSalesFunnelForm: FC<Props> = ({ onClose, editId }) => {
  const dispatch = useAppDispatch()

  const fetchingFunnel = useGetSalesFunnel(editId)
  const loading = !!editId && fetchingFunnel === 'pending'

  const {
    status: createStatus,
    execute: executeCreate,
    errors: createErrors,
  } = useCreateSalesFunnel()

  const {
    status: updateStatus,
    execute: executeUpdate,
    errors: updateErrors,
  } = useUpdateSalesFunnel()

  const errors = useMemo(
    () => (!!editId ? updateErrors : createErrors),
    [createErrors, editId, updateErrors],
  )

  const {
    name,
    isValid,
    allowOtherReasons,
    canSave,
    manualDisqualifyReasonCount,
  } = useSelector(selectEditFunnelDetail)
  const sections = useSelector(selectAllEditFunnelSections)

  const [remoteNameErrors, setRemoteNameErrors] = useState<string>()

  const disableSave =
    !canSave ||
    loading ||
    createStatus === 'pending' ||
    updateStatus == 'pending' ||
    !isValid

  const tryClose = useCallback(() => {
    if (createStatus != 'pending' && updateStatus != 'pending') onClose()
  }, [createStatus, onClose, updateStatus])

  const onNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRemoteNameErrors(undefined)
      dispatch(setEditFunnelName(e.target.value))
    },
    [dispatch],
  )

  const onAllowOtherChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      dispatch(setEditFunnelOtherReasons(checked))
    },
    [dispatch],
  )

  useEffect(() => {
    if (!editId) {
      dispatch(resetEditFunnel())
    }
  }, [dispatch, editId])

  useEffect(() => {
    if (createStatus == 'success') onClose()
    else if (updateStatus == 'success') onClose()
  }, [createStatus, onClose, updateStatus])

  useEffect(() => {
    if (fetchingFunnel === 'fail') {
      onClose()
      toast.error('Failed to load Sales Funnel')
    }
  }, [fetchingFunnel, onClose])

  useEffect(() => {
    setRemoteNameErrors(errors.get('name')?.join(' '))
  }, [errors])

  return (
    <SideDrawerForm
      onCancel={tryClose}
      onSave={!!editId ? executeUpdate : executeCreate}
      disableSave={disableSave}
      sx={{ ...(loading && styles.loadingContainer) }}
      saveBtnDataTestId="addSalesFunnelSaveBtn"
    >
      {loading ? (
        <CircularProgress color="primary" sx={styles.loader} />
      ) : (
        <>
          <Box sx={{ ...styles.form }}>
            {!!editId && (
              <Alert severity="info" sx={{ ...styles.editAlert }}>
                Edits will update all records
              </Alert>
            )}
            <Box sx={styles.section}>
              <Box sx={styles.sectionHeader}>
                <span style={styles.sectionLabel}>Sales Funnel Name</span>
              </Box>

              <Box sx={styles.row}>
                <TextField
                  sx={styles.textField}
                  required
                  size="small"
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={onNameChange}
                  inputProps={{
                    maxLength: 100,
                    'data-test-id': 'salesFunnelNameInput',
                  }}
                  error={!isValid || !!remoteNameErrors}
                  helperText={!isValid ? nameLengthError : remoteNameErrors}
                />
              </Box>

              <Box sx={styles.row}>
                <FormControlLabel
                  sx={{
                    marginBottom: (theme: Theme) => theme.spacing(0),
                    marginRight: (theme: Theme) => theme.spacing(0.8),
                  }}
                  label="Allow Field for Custom Reason"
                  control={
                    <Switch
                      checked={allowOtherReasons}
                      onChange={onAllowOtherChange}
                      color="primary"
                      disabled={manualDisqualifyReasonCount > 0}
                    />
                  }
                />
                <InfoTooltip infoText="Allow team members to provide a custom reason other than the pre-defined disqualified or closed/lost reasons (50 character limit)." />
              </Box>
            </Box>

            <SalesFunnelSectionList sections={sections} />
          </Box>
        </>
      )}
    </SideDrawerForm>
  )
}
